import type { ReactElement } from 'react';
import {
  BROWSER_AI_NAVIGATION_TYPES,
  BROWSER_AI_NAVIGATION_TYPE_LABELS,
  type BrowserAiNavigationType,
} from '@kirin/api-client/contracts/shadow-ai-browser-tools';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

/** Radix Select forbids an empty-string item value, so "all" is the sentinel. */
const ALL_NAV_TYPES = 'all';

const NAV_TYPE_ORDER: BrowserAiNavigationType[] = [
  BROWSER_AI_NAVIGATION_TYPES.REAL,
  BROWSER_AI_NAVIGATION_TYPES.REDIRECT,
  BROWSER_AI_NAVIGATION_TYPES.ADDRESS_BAR,
  BROWSER_AI_NAVIGATION_TYPES.ADS_TRACKING,
];

/**
 * Toolbar controls for the KIR-3890 evidence filters (Mockup 2): a single-select
 * "How it was reached" dropdown (navigation type) and a "Min visits" stepper.
 * State is owned by the caller so it can be threaded into the DataTable query
 * params + query key.
 */
export function BrowserToolEvidenceFilters({
  navigationType,
  onNavigationTypeChange,
  minVisits,
  onMinVisitsChange,
}: {
  navigationType: BrowserAiNavigationType | undefined;
  onNavigationTypeChange: (value: BrowserAiNavigationType | undefined) => void;
  minVisits: number | undefined;
  onMinVisitsChange: (value: number | undefined) => void;
}): ReactElement {
  return (
    <div className="flex items-center gap-4">
      <div className="flex items-center gap-2">
        <Label
          htmlFor="browser-tools-nav-type"
          className="text-sm font-normal text-muted-foreground"
        >
          How it was reached
        </Label>
        <Select
          value={navigationType ?? ALL_NAV_TYPES}
          onValueChange={value =>
            onNavigationTypeChange(
              value === ALL_NAV_TYPES
                ? undefined
                : (value as BrowserAiNavigationType)
            )
          }
        >
          <SelectTrigger
            id="browser-tools-nav-type"
            className="h-8 w-[200px]"
            aria-label="Filter by how the domain was reached"
          >
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_NAV_TYPES}>All types</SelectItem>
            {NAV_TYPE_ORDER.map(type => (
              <SelectItem key={type} value={type}>
                {BROWSER_AI_NAVIGATION_TYPE_LABELS[type]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-2">
        <Label
          htmlFor="browser-tools-min-visits"
          className="text-sm font-normal text-muted-foreground"
        >
          Min visits
        </Label>
        <Input
          id="browser-tools-min-visits"
          type="number"
          min={1}
          value={minVisits ?? ''}
          onChange={e => {
            const raw = e.target.value.trim();
            if (raw === '') {
              onMinVisitsChange(undefined);
              return;
            }
            const parsed = Number.parseInt(raw, 10);
            onMinVisitsChange(
              Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
            );
          }}
          className="h-8 w-20"
          placeholder="Any"
        />
      </div>
    </div>
  );
}

BrowserToolEvidenceFilters.displayName = 'BrowserToolEvidenceFilters';
